import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth, GRADES } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { ArrowLeft, Save, Edit3, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { sanitizeEmbed } from "../lib/sanitize";
import MarkdownContent from "../components/MarkdownContent.jsx";

export default function RitualDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { t, lang, pick } = useI18n();
  const otherLang = lang === "nl" ? "en" : "nl";
  const pickEmbed = (base) => {
    if (!ritual) return "";
    return ritual[`${base}_${lang}`] || ritual[`${base}_${otherLang}`] || "";
  };
  const [ritual, setRitual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [logging, setLogging] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/rituals/${slug}`);
        setRitual(data);
        setDraft({
          min_grade: data.min_grade,
          name_en: data.name_en, name_nl: data.name_nl,
          tagline_en: data.tagline_en, tagline_nl: data.tagline_nl,
          content_en: data.content_en, content_nl: data.content_nl,
          soundcloud_embed_en: data.soundcloud_embed_en || "",
          soundcloud_embed_nl: data.soundcloud_embed_nl || "",
          youtube_embed_en: data.youtube_embed_en || "",
          youtube_embed_nl: data.youtube_embed_nl || "",
        });
      } catch (e) {
        if (e.response?.status === 403) setForbidden(true);
        else toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const logToDiary = async () => {
    setLogging(true);
    const ritualName = pick(ritual, "name");
    try {
      await api.post("/diary", {
        content: t("diary.performed_template", { ritual: ritualName }),
        ritual_slug: ritual.slug,
        ritual_name: ritualName,
        entry_type: "ritual",
      });
      toast.success(t("diary.toast_inscribed", { ritual: ritualName }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("diary.toast_save_err"));
    } finally {
      setLogging(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/rituals/${slug}`, draft);
      setRitual(data);
      setEditing(false);
      toast.success(t("rituals.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("rituals.save_err"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-16">
        <p className="text-[#7A7061] italic">{t("rituals.unsealing")}</p>
      </main>
    );
  }
  if (forbidden) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="ritual-forbidden">
        <Link to="/rituals" className="btn-ghost inline-flex items-center gap-2 mb-8"><ArrowLeft size={14} /> {t("rituals.back_link")}</Link>
        <div className="border border-[#C1B49A] p-12 text-center">
          <h2 className="font-display text-3xl italic text-[#8B3A3A] mb-3">{t("lectures.forbidden_title")}</h2>
          <p className="text-[#4A4238]">{t("rituals.forbidden_body")}</p>
        </div>
      </main>
    );
  }
  if (!ritual) return null;

  const isAdmin = user?.role === "admin";
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <main data-testid="ritual-detail-page" className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <Link to="/rituals" className="btn-ghost inline-flex items-center gap-2 mb-8" data-testid="ritual-back-link">
        <ArrowLeft size={14} /> {t("rituals.back_link")}
      </Link>

      <article className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-12">
        <header className="mb-8 pb-8 etched-divider">
          {!editing ? (
            <>
              <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2"><Sparkles size={12} /> {t("rituals.sacred_working")} · <span className="text-[#4A4238]">{ritual.min_grade}+</span></p>
              <h1 className="font-display text-4xl sm:text-5xl text-[#1A1918] tracking-tight" data-testid="ritual-name">{pick(ritual, "name")}</h1>
              <p className="italic text-[#4A4238] text-lg mt-3" data-testid="ritual-tagline">{pick(ritual, "tagline")}</p>
            </>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="label-small block mb-2">{t("rituals.edit_min_grade")}</label>
                <select
                  data-testid="ritual-edit-min-grade"
                  className="bg-[#F4EEDF] border border-[#1A1918] px-3 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-none focus:outline-none focus:border-[#8B3A3A]"
                  value={draft.min_grade || "Probationer"}
                  onChange={(e) => setField("min_grade", e.target.value)}
                >
                  {GRADES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="label-small block mb-2">{t("rituals.edit_name_en")}</label>
                  <input data-testid="ritual-edit-name-en" className="input-etched font-display text-2xl" value={draft.name_en || ""} onChange={(e) => setField("name_en", e.target.value)} />
                </div>
                <div>
                  <label className="label-small block mb-2">{t("rituals.edit_name_nl")}</label>
                  <input data-testid="ritual-edit-name-nl" className="input-etched font-display text-2xl" value={draft.name_nl || ""} onChange={(e) => setField("name_nl", e.target.value)} />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="label-small block mb-2">{t("rituals.edit_tagline_en")}</label>
                  <input data-testid="ritual-edit-tagline-en" className="input-etched italic" value={draft.tagline_en || ""} onChange={(e) => setField("tagline_en", e.target.value)} />
                </div>
                <div>
                  <label className="label-small block mb-2">{t("rituals.edit_tagline_nl")}</label>
                  <input data-testid="ritual-edit-tagline-nl" className="input-etched italic" value={draft.tagline_nl || ""} onChange={(e) => setField("tagline_nl", e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </header>

        {!editing ? (
          <>
            <MarkdownContent testId="ritual-content">
              {pick(ritual, "content")}
            </MarkdownContent>
            {(pickEmbed("soundcloud_embed") || pickEmbed("youtube_embed")) && (
              <div className="mt-10 pt-8 etched-divider space-y-8">
                {pickEmbed("soundcloud_embed") && (
                  <div data-testid="ritual-soundcloud-section">
                    <p className="label-small text-[#8B3A3A] mb-3">{t("rituals.audio_heading")}</p>
                    <div
                      className="ritual-embed"
                      dangerouslySetInnerHTML={{ __html: sanitizeEmbed(pickEmbed("soundcloud_embed")) }}
                    />
                  </div>
                )}
                {pickEmbed("youtube_embed") && (
                  <div data-testid="ritual-youtube-section">
                    <p className="label-small text-[#8B3A3A] mb-3">{t("rituals.video_heading")}</p>
                    <div
                      className="ritual-embed ritual-embed-video"
                      dangerouslySetInnerHTML={{ __html: sanitizeEmbed(pickEmbed("youtube_embed")) }}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-6">
            <div>
              <label className="label-small block mb-2">{t("rituals.edit_content_en")}</label>
              <textarea data-testid="ritual-edit-content-en" rows={10} className="textarea-parchment" value={draft.content_en || ""} onChange={(e) => setField("content_en", e.target.value)} />
              <p className="text-xs italic text-[#7A7061] mt-1">{t("common.markdown_hint")}</p>
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.edit_content_nl")}</label>
              <textarea data-testid="ritual-edit-content-nl" rows={10} className="textarea-parchment" value={draft.content_nl || ""} onChange={(e) => setField("content_nl", e.target.value)} />
              <p className="text-xs italic text-[#7A7061] mt-1">{t("common.markdown_hint")}</p>
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.edit_soundcloud_en")}</label>
              <textarea data-testid="ritual-edit-soundcloud-en" rows={4} className="textarea-parchment font-mono text-xs" value={draft.soundcloud_embed_en || ""} onChange={(e) => setField("soundcloud_embed_en", e.target.value)} placeholder='<iframe ... src="https://w.soundcloud.com/..."></iframe>' />
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.edit_soundcloud_nl")}</label>
              <textarea data-testid="ritual-edit-soundcloud-nl" rows={4} className="textarea-parchment font-mono text-xs" value={draft.soundcloud_embed_nl || ""} onChange={(e) => setField("soundcloud_embed_nl", e.target.value)} placeholder='<iframe ... src="https://w.soundcloud.com/..."></iframe>' />
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.edit_youtube_en")}</label>
              <textarea data-testid="ritual-edit-youtube-en" rows={4} className="textarea-parchment font-mono text-xs" value={draft.youtube_embed_en || ""} onChange={(e) => setField("youtube_embed_en", e.target.value)} placeholder='<iframe ... src="https://www.youtube.com/embed/..."></iframe>' />
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.edit_youtube_nl")}</label>
              <textarea data-testid="ritual-edit-youtube-nl" rows={4} className="textarea-parchment font-mono text-xs" value={draft.youtube_embed_nl || ""} onChange={(e) => setField("youtube_embed_nl", e.target.value)} placeholder='<iframe ... src="https://www.youtube.com/embed/..."></iframe>' />
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <button data-testid="ritual-log-detail-btn" onClick={logToDiary} disabled={logging} className="btn-primary">
            {logging ? t("rituals.log_btn_busy") : t("rituals.log_btn")}
          </button>
          {isAdmin && (
            !editing ? (
              <button data-testid="ritual-edit-btn" onClick={() => setEditing(true)} className="btn-secondary inline-flex items-center gap-2">
                <Edit3 size={14} /> {t("common.edit")}
              </button>
            ) : (
              <div className="flex gap-3">
                <button data-testid="ritual-save-btn" onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2">
                  <Save size={14} /> {saving ? t("common.saving") : t("common.save")}
                </button>
                <button data-testid="ritual-cancel-btn" onClick={() => { setEditing(false); setDraft({ min_grade: ritual.min_grade, name_en: ritual.name_en, name_nl: ritual.name_nl, tagline_en: ritual.tagline_en, tagline_nl: ritual.tagline_nl, content_en: ritual.content_en, content_nl: ritual.content_nl, soundcloud_embed_en: ritual.soundcloud_embed_en || "", soundcloud_embed_nl: ritual.soundcloud_embed_nl || "", youtube_embed_en: ritual.youtube_embed_en || "", youtube_embed_nl: ritual.youtube_embed_nl || "" }); }} className="btn-secondary inline-flex items-center gap-2">
                  <X size={14} /> {t("common.cancel")}
                </button>
              </div>
            )
          )}
        </div>
      </article>
    </main>
  );
}
